import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png';

/**
 * Email-verification landing page.
 * Reached from signup confirmation emails as /verify-email?token=<uuid>.
 */
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = (searchParams.get('token') || '').trim();
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const [resendSentAt, setResendSentAt] = useState(null);  // HH:MM string
  // True when sessionStorage holds a pending paid-plan from the Pricing page.
  // We surface a transitional message and bounce the user to /auth so login
  // can pick up the pending_plan and resume Stripe checkout.
  const [hasPendingPlan, setHasPendingPlan] = useState(false);

  // Tick the cooldown down every second so users see exactly how long until
  // they can request another email. Cleared when it hits zero or unmount.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setErrorMsg('Verification link is missing a token.');
        setState('error');
        return;
      }
      try {
        const res = await fetch(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.ok) {
          setState('success');
          // If the user came from the Pricing page for a paid plan,
          // localStorage holds the pending_plan (with a 24h TTL). Show a
          // transitional message and bounce them to /auth so login can
          // resume checkout. Drop expired entries silently — the AuthPage
          // login flow is the canonical place to clear pending_plan.
          let pendingRaw = null;
          try { pendingRaw = localStorage.getItem('pending_plan'); } catch { /* private mode */ }
          if (pendingRaw) {
            try {
              const pending = JSON.parse(pendingRaw);
              if (!pending?.expires_at || pending.expires_at <= Date.now()) {
                try { localStorage.removeItem('pending_plan'); } catch { /* noop */ }
              } else {
                setHasPendingPlan(true);
                setTimeout(() => { if (!cancelled) navigate('/auth'); }, 2500);
              }
            } catch {
              try { localStorage.removeItem('pending_plan'); } catch { /* noop */ }
            }
          }
        } else {
          const data = await res.json().catch(() => ({}));
          setErrorMsg(data.detail || 'This verification link is invalid or has expired.');
          setState('error');
        }
      } catch {
        if (!cancelled) {
          setErrorMsg('Network error — please try again later.');
          setState('error');
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (resendCooldown > 0) return;
    const trimmed = resendEmail.trim();
    if (!trimmed) return;
    setResendBusy(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch {
      // Endpoint always returns 200 — surface the same outcome either way.
    } finally {
      setResendBusy(false);
      setResendDone(true);
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      setResendSentAt(`${hh}:${mm}`);
      setResendCooldown(60);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)' }}
      data-testid="verify-email-page"
    >
      <img src={SATISFIND_LOGO} alt="satisFIND" style={{ width: 160, height: 'auto' }} className="mb-8" />
      <Card className="w-full max-w-md border-[#E2E8F0] shadow-md">
        <CardHeader className="space-y-2">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto"
               style={{ backgroundColor: state === 'success' ? '#22C55E' : state === 'error' ? '#DC2626' : '#1A3E6F' }}>
            {state === 'verifying' && <Loader2 className="w-6 h-6 text-white animate-spin" />}
            {state === 'success' && <CheckCircle2 className="w-6 h-6 text-white" />}
            {state === 'error' && <XCircle className="w-6 h-6 text-white" />}
          </div>
          <CardTitle className="text-2xl font-['Montserrat'] text-[#0F172A] text-center">
            {state === 'verifying' && 'Verifying…'}
            {state === 'success' && 'Email Verified!'}
            {state === 'error' && 'Verification Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {state === 'verifying' && (
            <p className="text-sm text-[#64748B]">Hold on while we activate your account…</p>
          )}
          {state === 'success' && hasPendingPlan && (
            <div className="space-y-3" data-testid="verify-success-pending-plan">
              <p className="text-sm text-[#0F172A] font-medium">
                Your account is verified! Redirecting you to complete your subscription…
              </p>
              <div className="flex items-center justify-center gap-2 text-[#64748B] text-xs">
                <Loader2 className="w-4 h-4 animate-spin" /> Taking you to login
              </div>
            </div>
          )}
          {state === 'success' && !hasPendingPlan && (
            <>
              <p className="text-sm text-[#0F172A]">Your email has been verified!</p>
              <p className="text-sm text-[#64748B]">You can now log in to your satisFIND® account.</p>
              <Link to="/auth" data-testid="verify-success-login-link">
                <Button className="bg-[#1A3E6F] hover:bg-[#122c50] text-white w-full font-semibold">Go to Login</Button>
              </Link>
            </>
          )}
          {state === 'error' && (
            <>
              <p className="text-sm text-[#0F172A]" data-testid="verify-error-msg">
                {errorMsg || 'This verification link is invalid or has expired.'}
              </p>
              {resendDone ? (
                <div className="space-y-1" data-testid="resend-done-msg">
                  <p className="text-sm text-[#16A34A]">
                    If an account exists for that email, a new verification link has been sent.
                  </p>
                  {resendSentAt && (
                    <p className="text-xs text-[#64748B]" data-testid="resend-sent-at">
                      Sent at {resendSentAt}
                      {resendCooldown > 0 && <> · You can resend again in {resendCooldown}s</>}
                    </p>
                  )}
                </div>
              ) : resendOpen ? (
                <form onSubmit={handleResend} className="space-y-2 text-left" data-testid="resend-form">
                  <Input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    data-testid="resend-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={resendBusy || resendCooldown > 0}
                    className="bg-[#1A3E6F] hover:bg-[#122c50] text-white w-full font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="resend-submit-btn"
                  >
                    {resendBusy ? 'Sending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </Button>
                </form>
              ) : (
                <button
                  type="button"
                  className="text-sm text-[#1A3E6F] hover:underline"
                  onClick={() => setResendOpen(true)}
                  data-testid="resend-open-link"
                >
                  Resend verification email
                </button>
              )}
              <p className="text-sm text-[#64748B] inline-flex items-center justify-center gap-1.5">
                <Mail className="w-4 h-4" />
                Need help? <a href="mailto:support@satisfind.com" className="underline">support@satisfind.com</a>
              </p>
            </>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-[#94A3B8] mt-8">
        Powered by <span className="font-semibold">satisFIND®</span>
      </p>
    </div>
  );
}
